import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { useComputedColorScheme } from '@mantine/core';
import tokens from '../../../../design/tokens.json';
import { colors } from '@/const';
import { fmtCurrency } from '@/lib/currency';
import { fmtTokenCount, TokenUsageMap } from '@/lib/tokenUsage';

// recharts は SVG 属性で色を受け取り CSS 変数を解決しないので、スキームごとの値を渡す
function useChartColors() {
  const scheme = useComputedColorScheme('light');
  const { color } = tokens.shared;
  if (scheme === 'dark') {
    return {
      axis: color.dark.text.secondary,
      grid: color.dark.border,
      surface: color.dark.surfaceRaised,
      text: color.dark.text.primary,
    };
  }
  return {
    axis: color.scale.zinc[6],
    grid: color.border,
    surface: color.surface,
    text: color.text.primary,
  };
}

type BillingHistoryProps = {
  data: {
    startTime: number;
    endTime: number;
    billSpent: number;
  }[];
  year: number;
  month: number;
  currency: string;
};

export function BillingHistoryBar({ data, year, month, currency }: BillingHistoryProps) {
  const chart = useChartColors();
  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart
        data={data}
        margin={{
          top: 20,
          left: 10,
          right: 20,
          bottom: 20,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
        <XAxis
          dataKey="startTime"
          domain={[
            startOfMonth(new Date(year, month - 1)).getTime(),
            endOfMonth(new Date(year, month - 1)).getTime(),
          ]}
          type="number"
          tickFormatter={(t: number) => format(new Date(t), 'MM/dd')}
          tick={{ fontSize: 12, fill: chart.axis }}
          stroke={chart.grid}
        />
        <YAxis
          tickFormatter={(v: number) => fmtCurrency(v, currency)}
          tick={{ fontSize: 12, fill: chart.axis }}
          stroke={chart.grid}
        />
        <Tooltip
          labelFormatter={(t: number) => format(new Date(t), 'MM/dd')}
          formatter={(v: number) => fmtCurrency(v, currency)}
          contentStyle={{ backgroundColor: chart.surface, borderColor: chart.grid }}
          labelStyle={{ color: chart.text }}
          itemStyle={{ color: chart.text }}
        />
        <Legend wrapperStyle={{ color: chart.text }} />
        <Bar
          dataKey="billSpent"
          fill={colors.plamoBlue}
          activeBar={<Rectangle stroke={chart.text} />}
          name="利用料金"
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function BillingHistoryCumSumLine({ data, year, month, currency }: BillingHistoryProps) {
  const chart = useChartColors();
  return (
    <ResponsiveContainer width="100%" height={250}>
      <LineChart
        data={data}
        margin={{
          top: 20,
          left: 10,
          right: 20,
          bottom: 20,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
        <XAxis
          dataKey="startTime"
          domain={[
            startOfMonth(new Date(year, month - 1)).getTime(),
            endOfMonth(new Date(year, month - 1)).getTime(),
          ]}
          type="number"
          tickFormatter={(t: number) => format(new Date(t), 'MM/dd')}
          tick={{ fontSize: 12, fill: chart.axis }}
          stroke={chart.grid}
        />
        <YAxis
          tickFormatter={(v: number) => fmtCurrency(v, currency)}
          tick={{ fontSize: 12, fill: chart.axis }}
          stroke={chart.grid}
        />
        <Tooltip
          labelFormatter={(t: number) => format(new Date(t), 'MM/dd')}
          formatter={(v: number) => fmtCurrency(v, currency)}
          contentStyle={{ backgroundColor: chart.surface, borderColor: chart.grid }}
          labelStyle={{ color: chart.text }}
          itemStyle={{ color: chart.text }}
        />
        <Legend wrapperStyle={{ color: chart.text }} />
        <Line
          type="monotone"
          dataKey="billSpentSum"
          stroke={colors.plamoBlue}
          fill={colors.plamoBlue}
          name="利用料金(累計)"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

type Record = {
  startTime: number;
  endTime: number;
  tokenUsageSum: TokenUsageMap;
};

type TokenHistoryProps = {
  service: string;
  model: string;
  inputOrOutput: 'input_tokens' | 'output_tokens';
  data: Record[];
  year: number;
  month: number;
};

export function TokenHistoryCumSumLine({
  service,
  model,
  data,
  inputOrOutput,
  year,
  month,
}: TokenHistoryProps) {
  const chart = useChartColors();
  const strokeColors = {
    input_tokens: colors.plamoBlue2,
    output_tokens: colors.plamoPurple,
  };
  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart
        data={data}
        margin={{
          top: 20,
          left: 10,
          right: 20,
          bottom: 20,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
        <XAxis
          dataKey="startTime"
          domain={[
            startOfMonth(new Date(year, month - 1)).getTime(),
            endOfMonth(new Date(year, month - 1)).getTime(),
          ]}
          type="number"
          tickFormatter={(t: number) => format(new Date(t), 'MM/dd')}
          tick={{ fontSize: 12, fill: chart.axis }}
          stroke={chart.grid}
        />
        <YAxis
          tick={{ fontSize: 12, fill: chart.axis }}
          stroke={chart.grid}
          tickFormatter={(v: number) => fmtTokenCount(v)}
        />
        <Tooltip
          labelFormatter={(t: number) => format(new Date(t), 'MM/dd')}
          formatter={(v: number) => fmtTokenCount(v)}
          contentStyle={{ backgroundColor: chart.surface, borderColor: chart.grid }}
          labelStyle={{ color: chart.text }}
          itemStyle={{ color: chart.text }}
        />
        <Legend wrapperStyle={{ color: chart.text }} />
        <Line
          type="monotone"
          dataKey={(data: Record) => {
            return data.tokenUsageSum[service][model][inputOrOutput];
          }}
          stroke={strokeColors[inputOrOutput]}
          fill={strokeColors[inputOrOutput]}
          name={inputOrOutput === 'input_tokens' ? '入力トークン数' : '出力トークン数'}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
